import { css } from "@compiled/react";

import Checklist from "../../../components/checklist";
import { Image } from "../../../components/wpl/image/src";
import { Row } from "../../../libraries/wpl/foundations/src";

// ================================================
// Types
// ================================================

type ChecklistWithImageProps = {
    title: string;
    listItems: (string | JSX.Element)[];
    imageSrc: string;
    imageAlt: string;
    isIncludeTestimony?: boolean;
    quoteBlurb?: string;
    quoteName?: string;
    quoteCompany?: string;
    testimonyImageClassName?: string;
    testimonyImageSrc?: string;
    testimonyImageAlt?: string;
};

// ================================================
// CSS
// ================================================
const titleStyles = css({
    "marginBlockStart": 0,
    "fontWeight": 500,
    "color": "#253858",
    "letterSpacing": 0,
    "@media (min-width: 768px)": {
        fontSize: "2.25rem"
    }
});

const testimonyContainerStyle = css({
    "padding": "16px",
    ".testimony-row-container": {
        "margin": "15px auto",
        "padding": "16px",
        "@media (min-width: 768px)": {
            padding: "32px"
        },

        ".row-layout": {
            "borderRadius": "3px",
            "boxShadow": "1px 1px 6px #c1c7d0",
            "overflow": "hidden",
            "alignItems": "center",
            "grid-template-columns":
                "minmax(0px, 2.035fr) minmax(0px, 0.965fr)",
            "paddingInlineStart": "0",
            "paddingInlineEnd": "0",

            "@media (min-width: 0px) and (max-width: 767.98px)": {
                "grid-template-columns":
                    "minmax(0px, 2.035fr) minmax(0px, 0.965fr)",
                "paddingInlineStart": "0",
                "paddingInlineEnd": "0",
                "width": "100%"
            },
            "@media (min-width: 768px) and (max-width: 1023.98px)": {
                width: "700px",
                paddingInlineStart: "0",
                paddingInlineEnd: "0"
            },
            "@media (min-width: 1024px) and (max-width: 1439.98px)": {
                width: "800px",
                paddingInlineStart: "0",
                paddingInlineEnd: "0"
            },
            "@media (min-width: 1440px)": {
                width: "800px",
                paddingInlineStart: "0",
                paddingInlineEnd: "0"
            },

            ".quote-wrapper": {
                "display": "block",
                "padding": "32px",
                "@media (max-width: 767.98px)": {
                    display: "block"
                },

                ".quote-blurb": {
                    marginBlockStart: "16px",
                    marginBlockEnd: "32px"
                },

                ".quote-company": {
                    margin: "0"
                },

                ".quote-name": {
                    fontWeight: "600",
                    margin: "0"
                }
            }
        }
    },

    ".testimony-img": {
        "display": "flex",
        "flexDirection": "column",
        "justifyContent": "center",
        "alignItems": "center",
        "padding": "16px",
        "@media (min-width: 768px)": {
            padding: "32px"
        },
        "backgroundColor": "#f4f5f7",
        "alignSelf": "stretch"
    }
});

// ================================================
// Rendering Functions
// ================================================

const ChecklistWithImage = ({
    title,
    listItems,
    imageSrc,
    imageAlt,
    isIncludeTestimony = false,
    quoteBlurb,
    quoteName,
    quoteCompany,
    testimonyImageClassName,
    testimonyImageSrc,
    testimonyImageAlt
}: ChecklistWithImageProps) => {
    return (
        <section>
            <Row layout="4-8">
                <div>
                    <h3 css={titleStyles}>{title}</h3>
                    <Checklist listItems={listItems} />
                </div>
                <div>
                    <Image src={imageSrc} alt={imageAlt} />
                </div>
            </Row>
            {isIncludeTestimony && (
                <div css={testimonyContainerStyle}>
                    <div className="testimony-row-container">
                        <Row layout={"8-4"}>
                            <div className="testimony-quote">
                                <div className="quote-wrapper">
                                    <Image
                                        src={
                                            "https://wac-cdn.atlassian.com/dam/jcr:5412ebd3-6135-4109-9ffc-666961b9bad2/quote.svg?cdnVersion=1220"
                                        }
                                        alt={"Quotation mark"}
                                        className={"tab-quote-image"}
                                    />
                                    <p className="quote-blurb">{quoteBlurb}”</p>
                                    <p className="quote-name">{quoteName}</p>
                                    <p className="quote-company">
                                        {quoteCompany}
                                    </p>
                                </div>
                            </div>
                            <div className="testimony-img">
                                <Image
                                    src={testimonyImageSrc}
                                    alt={testimonyImageAlt}
                                    aspectRatio={"none"}
                                    className={testimonyImageClassName}
                                />
                            </div>
                        </Row>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ChecklistWithImage;
